import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import { formatAmount } from '../utils/helpers';
import { api, getPublicConfig } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { IconTrash, IconWhatsApp, IconEdit } from '../components/Icons';
import 'react-image-crop/dist/ReactCrop.css';

const ReceiptDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [photoFiles, setPhotoFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  // Phase 1: cached public config (publicUrl, companyName) so the
  // "Copy Verification URL" button reflects the operator's configured
  // URL instead of the legacy localStorage serverUrl.
  const [publicCfg, setPublicCfg] = useState({ publicUrl: '', companyName: 'ReceiptWise' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [deliveryMsg, setDeliveryMsg] = useState('');
  // Resolved email from the backend — falls back to the contact
  // directory when the receipt has no payer_email snapshot so the
  // Send button stays accurate.
  const [resolvedEmail, setResolvedEmail] = useState('');
  const [emailSource, setEmailSource] = useState('');
  // One-shot download-link state: button is busy while the backend mints
  // the token, then we open wa.me in a new tab.
  const [creatingLink, setCreatingLink] = useState(false);
  // Admin-only hard delete: confirmation modal + typed-id safety check.
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  // Edit payer info (name/phone/email). Opens a modal pre-filled with
  // current values; PUT /api/receipts/:id persists any changed fields.
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ payer: '', payer_phone: '', payer_email: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // Fetch the public config once on mount and again whenever the
  // window regains focus — so the "Copy Verification URL" button
  // reflects the operator's current PublicURL setting without a full
  // reload after the admin changes it on the Config page.
  useEffect(() => {
    let cancelled = false;
    const fetch = () => getPublicConfig().then(c => { if (!cancelled) setPublicCfg(c); }).catch(() => {});
    fetch();
    window.addEventListener('focus', fetch);
    return () => { cancelled = true; window.removeEventListener('focus', fetch); };
  }, []);
  
  // Crop state
  const [cropping, setCropping] = useState(false);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [imageRef, setImageRef] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [originalFilename, setOriginalFilename] = useState(null);
  const [compressionQuality, setCompressionQuality] = useState(0.9);
  const [grayscale, setGrayscale] = useState(false);
  const [rotation, setRotation] = useState(0);
  const imgRef = useRef(null);

    useEffect(() => {
    fetchReceipt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchReceipt = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/receipts/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch receipt');
      }
      
      const data = await response.json();
      // data shape is `{ receipt, resolved_email, email_source }` —
      // the backend's ReceiptResponse includes the resolved email
      // (snapshot → payer_contact_id → contact-name lookup) so the
      // Send-email button here matches what the handler will
      // actually use at send-time, not just the snapshot.
      setReceipt(data.receipt);
      setResolvedEmail(data.resolved_email || '');
      setEmailSource(data.email_source || '');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Crop helper functions
  function centerAspectCrop(mediaWidth, mediaHeight) {
    return centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        4 / 3,
        mediaWidth,
        mediaHeight,
      ),
      mediaWidth,
      mediaHeight,
    );
  }

  function fullImageCrop(mediaWidth, mediaHeight) {
    return {
      unit: '%',
      width: 100,
      height: 100,
      x: 0,
      y: 0,
    };
  }

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const file = files[0];
      setOriginalFilename(file.name);
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewFile(reader.result);
        setCropping(true);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle paste from clipboard
  const handlePaste = useCallback(async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    
    for (let item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          setOriginalFilename(file.name || 'pasted-image.png');
          const reader = new FileReader();
          reader.onload = () => {
            setPreviewFile(reader.result);
            setCropping(true);
          };
          reader.readAsDataURL(file);
        }
        return;
      }
    }
  }, []);

  // Listen for paste events on the document
  useEffect(() => {
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [handlePaste]);

  const handleCropComplete = () => {
    if (!completedCrop || !imageRef) return;
    
    const canvas = document.createElement('canvas');
    const scaleX = imageRef.naturalWidth / imageRef.width;
    const scaleY = imageRef.naturalHeight / imageRef.height;
    
    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;
    
    const ctx = canvas.getContext('2d');
    
    // Apply grayscale if selected
    if (grayscale) {
      ctx.filter = 'grayscale(100%)';
    }
    
    // Handle rotation — draw cropped region with rotation applied
    if (rotation !== 0) {
      const cropX = completedCrop.x * scaleX;
      const cropY = completedCrop.y * scaleY;
      const cropW = completedCrop.width * scaleX;
      const cropH = completedCrop.height * scaleY;

      canvas.width = rotation === 90 || rotation === 270 ? cropH : cropW;
      canvas.height = rotation === 90 || rotation === 270 ? cropW : cropH;

      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.drawImage(
        imageRef,
        cropX, cropY, cropW, cropH,
        -cropW / 2, -cropH / 2, cropW, cropH
      );
      ctx.setTransform(1, 0, 0, 1, 0, 0);
    } else {
      ctx.drawImage(
        imageRef,
        completedCrop.x * scaleX,
        completedCrop.y * scaleY,
        completedCrop.width * scaleX,
        completedCrop.height * scaleY,
        0,
        0,
        canvas.width,
        canvas.height
      );
    }
    
    canvas.toBlob((blob) => {
      // Keep original filename or generate new one
      const baseName = originalFilename 
        ? originalFilename.replace(/\.[^/.]+$/, '') 
        : 'photo';
      const croppedFileName = baseName + '_cropped.jpg';
      const croppedFile = new File([blob], croppedFileName, { type: 'image/jpeg' });
      setPhotoFiles([croppedFile]);
      setCropping(false);
      setPreviewFile(null);
      setOriginalFilename(null);
      setGrayscale(false);
      setRotation(0);
    }, 'image/jpeg', compressionQuality);
  };

  const handleRotateLeft = () => {
    setRotation((rotation - 90) % 360);
  };

  const handleRotateRight = () => {
    setRotation((rotation + 90) % 360);
  };

  const handleCancelCrop = () => {
    setCropping(false);
    setPreviewFile(null);
    setCrop(undefined);
  };


  const handlePhotoDoubleClick = (photo) => {
    const url = `/uploads/${photo}`;
    window.open(url, '_blank');
  };

  const handlePhotoUpload = async () => {
    if (photoFiles.length === 0) return;
    
    setUploading(true);
    setUploadProgress(0);
    
    const formData = new FormData();
    for (let i = 0; i < photoFiles.length; i++) {
      formData.append('photos', photoFiles[i]);
    }
    
    try {
      const token = localStorage.getItem('token');
      
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(percent);
        }
      });
      
      xhr.addEventListener('load', () => {
        setUploading(false);
        if (xhr.status === 200) {
          const data = JSON.parse(xhr.responseText);
          // Merge new photo paths with existing ones
          const currentPaths = receipt.photo_paths || [];
          const newPaths = data.photo_paths || [];
          setReceipt({ ...receipt, photo_paths: [...currentPaths, ...newPaths] });
          setPhotoFiles([]);
          setUploadProgress(0);
        } else {
          alert('Failed to upload photos');
        }
      });
      
      xhr.addEventListener('error', () => {
        setUploading(false);
        alert('Failed to upload photos');
      });
      
      xhr.open('POST', `/api/receipts/${id}/photo`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);
      
    } catch (err) {
      setUploading(false);
      alert(err.message);
    }
  };

  const handlePhotoDelete = async (photoToDelete) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`/api/receipts/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      const currentReceipt = data.receipt;
      
      // Filter out the photo to delete
      const currentPaths = currentReceipt.photo_paths || [];
      const newPaths = currentPaths.filter(p => p !== photoToDelete);
      
      const updateResponse = await fetch(`/api/receipts/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...currentReceipt,
          photo_paths: newPaths
        })
      });
      
      if (!updateResponse.ok) {
        throw new Error('Failed to delete photo');
      }
      
      const updateData = await updateResponse.json();
      setReceipt(updateData.receipt);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleExportPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      const serverUrl = localStorage.getItem('serverUrl') || '';
      
      const response = await fetch(`/api/receipts/${id}/export?serverUrl=${encodeURIComponent(serverUrl)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to export');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleCopyURL = async () => {
    // Use the operator-configured publicUrl from /api/config, NOT the
    // legacy localStorage serverUrl. The configured URL is what shows
    // up on the PDF QR code and in WhatsApp messages, so the Copy
    // button must match that to avoid the "link copied doesn't match
    // the QR" surprise.
    const base = publicCfg.publicUrl || localStorage.getItem('serverUrl') || '';
    const url = `${base}/api/receipts/${receipt.sha256_hash}/verify`;
    try {
      await navigator.clipboard.writeText(url);
      alert('Verification URL copied to clipboard!');
    } catch (err) {
      // Fallback
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      alert('Verification URL copied to clipboard!');
    }
  };

  const handleSendEmail = async () => {
    setDeliveryMsg('');
    setSendingEmail(true);
    try {
      const r = await api.sendReceiptEmail(receipt.id);
      setDeliveryMsg(r.message || '✓ Email enviado');
    } catch (err) {
      setDeliveryMsg('✗ ' + err.message);
    } finally {
      setSendingEmail(false);
      setTimeout(() => setDeliveryMsg(''), 6000);
    }
  };

  // Send via WhatsApp: mints a one-shot, 14-day-expiry download link
  // on the backend and opens wa.me with the link prefilled in the
  // message. The backend invalidates any previous active link for this
  // receipt so this is always the latest, only-valid link. We keep the
  // returned {url, expires_at} in state so the success banner can show
  // the operator when their link will stop working.
  const handleSendViaWhatsApp = async () => {
    if (!receipt) return;
    setDeliveryMsg('');
    setCreatingLink(true);
    try {
      const r = await api.createDownloadLink(receipt.id);
      // Prefer the backend's pre-built wa_url (it normalizes the phone
      // and encodes the message). Fall back to a plain wa.me link only
      // if the backend didn't return one (e.g. receipt has no phone).
      const waURL = r.wa_url || `https://wa.me/?text=${encodeURIComponent(r.url)}`;
      window.open(waURL, '_blank', 'noopener,noreferrer');
      const exp = new Date(r.expires_at);
      const expStr = exp.toLocaleDateString();
      setDeliveryMsg(`✓ Link listo. Válido hasta ${expStr} o hasta que lo descarguen.`);
    } catch (err) {
      setDeliveryMsg('✗ ' + (err.message || 'No se pudo generar el link'));
    } finally {
      setCreatingLink(false);
      setTimeout(() => setDeliveryMsg(''), 8000);
    }
  };

  // Admin-only hard delete. Backend requires system_admin role; we also
  // require the operator to type the receipt ID in the confirmation
  // modal so a misclick doesn't permanently erase a receipt.
  const openDeleteModal = () => {
    setDeleteConfirmText('');
    setDeleteError('');
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (deleting) return; // don't allow close mid-delete
    setShowDeleteModal(false);
    setDeleteConfirmText('');
    setDeleteError('');
  };

  const handleDelete = async () => {
    if (!receipt) return;
    if (deleteConfirmText !== String(receipt.id)) {
      setDeleteError(`Type the receipt ID (${receipt.id}) to confirm.`);
      return;
    }
    setDeleting(true);
    setDeleteError('');
    try {
      await api.deleteReceipt(receipt.id);
      // Hard delete — receipt is gone. Audit log on the backend has
      // already captured who/when/what. Send the operator back to the
      // list.
      navigate('/', { replace: true });
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete receipt');
      setDeleting(false);
    }
  };

  // Edit payer info (name/phone/email). The form only sends fields that
  // changed — the backend uses *string pointers to distinguish "not
  // touched" from "set to empty string", so emptying a phone is a real
  // operation, not a no-op. After save we patch local state from the
  // server response so the Email / WhatsApp buttons render correctly
  // without a hard reload.
  const openEditModal = () => {
    setEditForm({
      payer:      receipt.payer || '',
      payer_phone: receipt.payer_phone || '',
      payer_email: receipt.payer_email || '',
    });
    setEditError('');
    setShowEditModal(true);
  };
  const closeEditModal = () => {
    if (editSaving) return;
    setShowEditModal(false);
    setEditError('');
  };
  const handleEditSave = async () => {
    if (!receipt) return;
    setEditSaving(true);
    setEditError('');
    try {
      // api.updateReceipt already exists; it does PUT /api/receipts/:id.
      const updated = await api.updateReceipt(receipt.id, {
        payer:       editForm.payer,
        payer_phone: editForm.payer_phone,
        payer_email: editForm.payer_email,
      });
      // The backend returns the full receipt; patch local state.
      setReceipt(updated.receipt || updated);
      setShowEditModal(false);
      setDeliveryMsg('✓ Payer info updated');
      setTimeout(() => setDeliveryMsg(''), 4000);
    } catch (err) {
      setEditError(err.message || 'Failed to update payer info');
    } finally {
      setEditSaving(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    setAddingComment(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/receipts/${id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content: newComment })
      });
      
      if (!response.ok) {
        throw new Error('Failed to add comment');
      }
      
      const comment = await response.json();
      const currentComments = receipt.comments || [];
      setReceipt({ ...receipt, comments: [...currentComments, comment] });
      setNewComment('');
    } catch (err) {
      alert(err.message);
    } finally {
      setAddingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    // Ensure commentId is a string to preserve precision
    const commentIdStr = String(commentId);
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    
    try {
      const token = localStorage.getItem('token');
      console.log('Sending delete for receipt:', id);
      const response = await fetch(`/api/receipts/${id}/comments`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ comment_id: commentIdStr })
      });
      
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete comment');
      }
      
      const currentComments = receipt.comments || [];
      setReceipt({ ...receipt, comments: currentComments.filter(c => c.id !== commentId) });
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) return <div className="loading"><div className="spinner"></div></div>;
  if (error) return <div className="error">{error}</div>;
  if (!receipt) return <div className="error">Receipt not found</div>;

  const photoPaths = receipt.photo_paths || [];

  return (
    <div className="receipt-detail">
      <div className="receipt-actions">
        <Link to="/" className="btn btn-secondary">← Back to List</Link>
        <button onClick={handleExportPDF} className="btn btn-primary">
          Download Receipt
        </button>
        <button
          onClick={openEditModal}
          className="btn btn-outline"
          title="Edit payer info (name, phone, email)"
        >
          <IconEdit size={16} /> Edit payer
        </button>
        <button onClick={handleCopyURL} className="btn btn-outline">
          📋 Copy Verification URL
        </button>
        {resolvedEmail && (
          <button
            onClick={handleSendEmail}
            className="btn btn-outline"
            disabled={sendingEmail || receipt.status === 'cancelled'}
            title={
              receipt.status === 'cancelled'
                ? 'Recibo cancelado'
                : emailSource === 'snapshot'
                  ? `Enviar PDF por email a ${resolvedEmail}`
                  : `Enviar PDF por email a ${resolvedEmail} (resuelto del contacto)`
            }
          >
            {sendingEmail ? 'Enviando…' : '✉️ Enviar por Email'}
          </button>
        )}
        <button
          onClick={handleSendViaWhatsApp}
          className="btn btn-outline"
          disabled={receipt.status === 'cancelled' || creatingLink}
          title={
            receipt.status === 'cancelled'
              ? 'Recibo cancelado'
              : 'Genera un link de descarga único (válido 14 días o hasta que lo descarguen) y abre WhatsApp listo para enviar'
          }
        >
          <IconWhatsApp size={16} /> {creatingLink ? 'Generando…' : 'Send via WhatsApp'}
        </button>
        {user && user.role === 'system_admin' && (
          <button
            onClick={openDeleteModal}
            className="btn btn-danger"
            title="Permanently delete this receipt (system_admin only)"
          >
            <IconTrash size={16} /> Delete (Admin)
          </button>
        )}
      </div>
      {deliveryMsg && (
        <div style={{
          margin: '.75rem 0',
          padding: '.6rem .9rem',
          borderRadius: 8,
          background: deliveryMsg.startsWith('✗') ? 'var(--danger-bg, #fee2e2)' : 'var(--success-bg, #dcfce7)',
          color: deliveryMsg.startsWith('✗') ? 'var(--danger, #991b1b)' : 'var(--success, #166534)',
          fontSize: '.9rem'
        }}>
          {deliveryMsg}
        </div>
      )}
      
      <div className="receipt-document">
        <div className="receipt-title-bar">
          <h1>RECEIPT</h1>
          <span className={`receipt-status ${receipt.status}`}>
            {receipt.status === 'cancelled' ? 'CANCELLED' : 'VALID'}
          </span>
        </div>
        
        <div className="receipt-number">Receipt #{receipt.id}</div>
        
        <table className="receipt-table">
          <tbody>
            <tr>
              <th>Date</th>
              <td>{new Date(receipt.date).toLocaleDateString()}</td>
              <th>Amount</th>
              <td className="amount-cell">{formatAmount(receipt.amount, receipt.type)}</td>
            </tr>
            <tr>
              <th>From (Payer)</th>
              <td>{receipt.sender}</td>
              <th>To (Recipient)</th>
              <td>{receipt.recipient}</td>
            </tr>
            <tr>
              <th>Concept</th>
              <td colSpan="3">{receipt.concept}</td>
            </tr>
            {receipt.description && (
              <tr>
                <th>Description</th>
                <td colSpan="3">{receipt.description}</td>
              </tr>
            )}
            <tr>
              <th>Payment Method</th>
              <td colSpan="3">{receipt.payment_method}</td>
            </tr>
            {receipt.status === 'cancelled' && (
              <>
                <tr className="cancel-row">
                  <th>Cancelled At</th>
                  <td colSpan="3">{new Date(receipt.canceled_at).toLocaleString()}</td>
                </tr>
                <tr className="cancel-row">
                  <th>Cancel Reason</th>
                  <td colSpan="3">{receipt.cancel_reason}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
        
        <div className="receipt-hash-section">
          <div className="hash-label">SHA-256 Verification Hash</div>
          <code className="receipt-hash">{receipt.sha256_hash}</code>
        </div>
        
        <div className="receipt-meta">
          Created: {new Date(receipt.created_at).toLocaleString()}
        </div>
      </div>
      
      {/* Comments Section */}
      <div className="comments-section">
        <h3>Comments ({(receipt.comments || []).length})</h3>
        
        {/* Add new comment */}
        <div className="add-comment">
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment..."
            rows={6}
            className="comment-textarea"
          />
          <button 
            onClick={handleAddComment} 
            disabled={addingComment || !newComment.trim()}
            className="btn btn-primary"
          >
            {addingComment ? 'Adding...' : 'Add Comment'}
          </button>
        </div>
        
        {/* Show existing comments */}
        {(receipt.comments || []).length > 0 ? (
          <div className="comments-list">
            {receipt.comments.map((comment) => (
              <div key={comment.id} className="comment">
                <div className="comment-header">
                  <strong>{comment.username}</strong>
                  <span className="comment-date">
                    {new Date(comment.created_at).toLocaleString()}
                  </span>
                  <button 
                    onClick={() => handleDeleteComment(String(comment.id))} 
                    className="btn-delete-comment"
                    title="Delete comment"
                  >
                    <IconTrash size={14} />
                  </button>
                </div>
                <div className="comment-content">{comment.content}</div>
              </div>
            ))}
          </div>
        ) : (
          <p className="no-comments">No comments yet</p>
        )}
      </div>
      
      {/* Crop Modal */}
      {cropping && previewFile && (
        <div className="crop-modal">
          <div className="crop-container">
            <h3>Crop Photo</h3>
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={undefined}
              style={{ 
                  filter: grayscale ? 'grayscale(100%)' : 'none',
                  transform: `rotate(${rotation}deg)`,
                  transition: 'transform 0.3s ease'
                }}
            >
              <img
                ref={(ref) => {
                  setImageRef(ref);
                  imgRef.current = ref;
                }}
                src={previewFile}
                alt="Crop preview"
                onLoad={(e) => {
                  const { width, height } = e.target;
                  setCrop(centerAspectCrop(width, height));
                }}
              />
            </ReactCrop>
            <div className="crop-options">
              <div className="crop-quality">
                <label>Quality: {Math.round(compressionQuality * 100)}%</label>
                <input
                  type="range"
                  min="0.1"
                  max="1"
                  step="0.1"
                  value={compressionQuality}
                  onChange={(e) => setCompressionQuality(parseFloat(e.target.value))}
                />
              </div>
              <div className="crop-grayscale">
                <label>
                  <input
                    type="checkbox"
                    checked={grayscale}
                    onChange={(e) => setGrayscale(e.target.checked)}
                  />
                  Convert to Grayscale
                </label>
              </div>
              <div className="crop-rotate">
                <button onClick={handleRotateLeft} className="btn btn-sm btn-outline" title="Rotate Left">
                  ↺ Rotate Left
                </button>
                <button onClick={handleRotateRight} className="btn btn-sm btn-outline" title="Rotate Right">
                  ↻ Rotate Right
                </button>
              </div>
              <div className="crop-select-all">
                <button 
                  onClick={() => {
                    if (imageRef) {
                      const { width, height } = imageRef;
                      setCrop(fullImageCrop(width, height));
                      setCompletedCrop(fullImageCrop(width, height));
                    }
                  }} 
                  className="btn btn-sm btn-outline"
                  title="Select the entire image"
                >
                  ☐ Select All
                </button>
              </div>
            </div>
            <div className="crop-buttons">
              <button onClick={handleCancelCrop} className="btn btn-secondary">
                Cancel
              </button>
              <button onClick={handleCropComplete} className="btn btn-primary">
                Apply Crop
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="photo-section">
        <h3>Photo Evidence ({photoPaths.length})</h3>
        
        {/* Show existing photos */}
        {photoPaths.length > 0 && (
          <div className="photos-grid">
            {photoPaths.map((photo, index) => (
              <div key={index} className="photo-container">
                <img 
                  src={`/uploads/${photo}?t=${Date.now()}`} 
                  alt={`Receipt evidence ${index + 1}`} 
                  className="receipt-photo"
                  onDoubleClick={() => handlePhotoDoubleClick(photo)}
                  style={{ cursor: 'pointer' }}
                  title="Double-click to view full size"
                />
                <button 
                  onClick={() => handlePhotoDelete(photo)} 
                  className="btn-delete-photo"
                  title="Delete photo"
                >
                  <IconTrash size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
        
        {/* Upload new photos */}
        <div className="no-photo">
          {photoPaths.length === 0 && <p>No photos uploaded</p>}
          
          <div className="photo-buttons">
            <label className="btn btn-secondary">
              📁 Choose Files
              <input 
                type="file" 
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </label>
            <label className="btn btn-primary">
              📷 Take Photo
              <input 
                type="file" 
                accept="image/*" 
                capture="environment"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </label>
            <button className="btn btn-secondary" onClick={async () => {
              try {
                const items = await navigator.clipboard.read();
                for (const item of items) {
                  for (const type of item.types) {
                    if (type.startsWith('image/')) {
                      const blob = await item.getType(type);
                      const file = new File([blob], 'clipboard-image.png', { type });
                      setOriginalFilename('clipboard-image.png');
                      const reader = new FileReader();
                      reader.onload = () => {
                        setPreviewFile(reader.result);
                        setCropping(true);
                      };
                      reader.readAsDataURL(file);
                      return;
                    }
                  }
                }
                alert('No image found in clipboard');
              } catch (err) {
                console.error('Paste error:', err);
                // Fallback: try the old way
                alert('Tip: You can also paste (Ctrl+V) directly on this page!');
              }
            }}>
              📋 Paste
            </button>
          </div>
          <p style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.5rem' }}>
            💡 Tip: Press Ctrl+V (or Cmd+V) anywhere on this page to paste an image from clipboard
          </p>
          
          {photoFiles.length > 0 && (
            <div className="selected-files">
              <p>Selected: {photoFiles.length} file(s)</p>
              <ul>
                {photoFiles.map((file, index) => (
                  <li key={index}>{file.name}</li>
                ))}
              </ul>
              <button 
                onClick={handlePhotoUpload} 
                disabled={uploading} 
                className="btn btn-secondary"
                style={{ 
                  backgroundColor: photoFiles.length > 0 ? '#dc2626' : undefined,
                  borderColor: photoFiles.length > 0 ? '#dc2626' : undefined,
                  color: photoFiles.length > 0 ? '#fff' : undefined
                }}
              >
                {uploading ? 'Uploading...' : 'Upload Photos'}
              </button>
              <button 
                onClick={() => setPhotoFiles([])} 
                className="btn btn-outline"
                style={{ marginLeft: '8px' }}
              >
                Clear
              </button>
              {uploading && (
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${uploadProgress}%` }}>
                    {uploadProgress}%
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Edit payer info modal — lets the operator add a phone/email so
          the Email / WhatsApp buttons can fire. Available on every receipt
          regardless of cancellation status (cancelled receipts just can't
          actually send). */}
      {showEditModal && receipt && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <h2><IconEdit size={18} /> Edit payer info — Receipt #{receipt.id}</h2>
              <button className="modal-close" onClick={closeEditModal}>×</button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); handleEditSave(); }}>
              <div className="modal-body">
                {editError && <div className="alert alert-error">{editError}</div>}
                <p style={{ marginTop: 0, color: 'var(--gray-600)', fontSize: '0.9rem' }}>
                  Add a phone and/or email here so the Email and WhatsApp buttons on
                  this page work. Empty fields are allowed — they clear the value.
                </p>
                <div className="form-group">
                  <label>Payer name</label>
                  <input
                    type="text"
                    value={editForm.payer}
                    onChange={(e) => setEditForm({ ...editForm, payer: e.target.value })}
                    disabled={editSaving}
                    placeholder="e.g. Acme SA de CV"
                  />
                </div>
                <div className="form-group">
                  <label>Payer phone (with country code)</label>
                  <input
                    type="tel"
                    value={editForm.payer_phone}
                    onChange={(e) => setEditForm({ ...editForm, payer_phone: e.target.value })}
                    disabled={editSaving}
                    placeholder="+525512345678"
                  />
                </div>
                <div className="form-group">
                  <label>Payer email</label>
                  <input
                    type="email"
                    value={editForm.payer_email}
                    onChange={(e) => setEditForm({ ...editForm, payer_email: e.target.value })}
                    disabled={editSaving}
                    placeholder="cobros@acme.mx"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeEditModal} disabled={editSaving}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={editSaving}>
                  {editSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Admin hard-delete confirmation modal — requires typing the receipt ID. */}
      {showDeleteModal && receipt && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <h2 style={{ color: 'var(--danger, #991b1b)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><IconTrash size={20} /> Delete receipt #{receipt.id}?</h2>
              <button className="modal-close" onClick={closeDeleteModal}>×</button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); handleDelete(); }}>
              <div className="modal-body">
                {deleteError && <div className="alert alert-error">{deleteError}</div>}
                <p style={{ marginTop: 0 }}>
                  This will <strong>permanently</strong> delete receipt <strong>#{receipt.id}</strong>
                  {' '}for <strong>{formatAmount(receipt.amount, receipt.type)}</strong>
                  {receipt.payer && <> from <strong>{receipt.payer}</strong></>}
                  {receipt.recipient && <> to <strong>{receipt.recipient}</strong></>}.
                </p>
                <p style={{ color: 'var(--danger, #991b1b)', fontWeight: 600 }}>
                  Cancel is reversible (the receipt stays in the database). Delete is not — once
                  confirmed, the receipt and its comments are removed from the database immediately.
                  An audit-log entry is written before deletion.
                </p>
                <div className="form-group">
                  <label>
                    Type <code>{receipt.id}</code> to confirm:
                  </label>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder={String(receipt.id)}
                    autoFocus
                    disabled={deleting}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeDeleteModal}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-danger"
                  disabled={deleting || deleteConfirmText !== String(receipt.id)}
                >
                  {deleting ? 'Deleting…' : 'Delete permanently'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceiptDetail;
